"use client"
import { getTheme } from "@/theme"
import {
    Box,
    Typography,
    Stack,
    CardContent,
    SxProps,
    alpha,
    Chip,
    Theme,
    useMediaQuery,
} from "@mui/material"
import { Card } from "@mui/material"
import Image from "next/image"
import { FC, ReactNode } from "react"
import { BillingPeriod } from "@recall/payment"
import { ButtonProps } from "./buttons/types"

const PlanId = {
    LITE: "lite",
    PLUS: "plus",
    BUSINESS: "business",
} as const

export interface Plan {
    id: (typeof PlanId)[keyof typeof PlanId]
    name: string
    icon: string
    monthly?: string
    yearly?: string
    subheading: string
    featuresSubHeading: string
    features: { content: ReactNode; highlight?: boolean }[]
    tag?: string
    buttonComponent: FC<ButtonProps>
}

type Props = {
    plan: Plan
    priceKey: BillingPeriod
}

export const PricingCard: FC<Props> = ({ plan, priceKey }) => {
    const isMobile = useMediaQuery(theme.breakpoints.down("lg")) ? 1.25 : 1
    return (
        <Card
            sx={
                plan.tag === "Popular"
                    ? {
                          ...styles.card,
                          border: (theme) => `1px solid ${theme.palette.primary.main}`,
                      }
                    : styles.card
            }
        >
            <CardContent sx={styles.cardContent}>
                {plan.id == PlanId.PLUS && (
                    <Box sx={styles.cardGlow}>
                        <Image
                            src="/svgs/backgrounds/pvc-card-bg-two.svg"
                            alt="card glow"
                            width={364 * isMobile}
                            height={245 * isMobile}
                            priority={false}
                        />
                    </Box>
                )}
                <Image src={plan.icon} alt={plan.name} width={40} height={40} />
                <Typography variant="h6" sx={styles.planName} py={!plan.tag ? "4px" : "0"}>
                    {plan.name}
                    {plan.tag && (
                        <Chip
                            component="span"
                            label={plan.tag}
                            size="small"
                            sx={styles.popularChip}
                        />
                    )}
                </Typography>
                <Box sx={styles.priceContainer}>
                    <Typography variant="h4" sx={styles.price}>
                        {plan[priceKey as keyof Plan]
                            ? `$${plan[priceKey as keyof Plan]}`
                            : "Custom pricing"}
                    </Typography>
                    {plan[priceKey as keyof Plan] && (
                        <Typography variant="caption">per month</Typography>
                    )}
                </Box>
                <Typography variant="body1" mb={3}>
                    {plan.subheading}
                </Typography>

                <plan.buttonComponent billingPeriod={priceKey} />

                <Box sx={styles.divider}>
                    <Image src="/svgs/border-line.svg" alt="border-line" fill={true} />
                </Box>
                <Typography
                    component="p"
                    variant="subtitle1"
                    color={theme.palette.text.secondary}
                    mb={2.25}
                >
                    {plan.featuresSubHeading}
                </Typography>
                <Stack spacing={1} mb={3}>
                    {plan.features.map((feature) => (
                        <Box key={feature?.toString()} display="flex" gap={1.25}>
                            <Image
                                src={feature.highlight ? "svgs/flame.svg" : "/svgs/check.svg"}
                                alt="check"
                                width={20}
                                height={21}
                                style={{ marginTop: 4 }}
                            />
                            <Typography variant="subtitle1" color="white">
                                {feature.content}
                            </Typography>
                        </Box>
                    ))}
                </Stack>
            </CardContent>
        </Card>
    )
}

const theme = getTheme()

const styles: Record<string, SxProps<Theme>> = {
    card: {
        backgroundColor: (theme) => theme.palette.background.default,
        borderRadius: 1.25,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
        transition: "border-color 0.3s",
        height: "100%",
    },
    cardContent: {
        position: "relative",
        p: 3.75,
        textAlign: "left",
    },
    cardGlow: {
        position: "absolute",
        width: "100%",
        aspectRatio: 1.9,
        top: 0,
        right: 0,
    },
    planName: {
        lineHeight: "normal",
        fontWeight: 600,
        mb: 3.75,
        mt: 2,
    },
    popularChip: {
        ml: 2,
        p: (theme) => theme.spacing(1.5, 0.5),
        fontWeight: "500",
        color: (theme) => theme.palette.info.light,
        backgroundColor: (theme) => alpha(theme.palette.info.light, 0.2),
        border: `1px solid ${alpha(theme.palette.info.light, 0.3)}`,
    },
    priceContainer: {
        display: "flex",
        alignItems: "baseline",
        gap: 1,
        mb: 2.5,
    },
    price: {
        fontWeight: 700,
        mb: 1,
        lineHeight: 1.2,
    },
    divider: {
        my: 3.75,
        width: "100%",
        height: 2,
        position: "relative",
    },
}
